import React, { useState, useEffect, useContext, useMemo } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, MenuItem, Select, TextField } from "@mui/material";
import LoadingButton from '@mui/lab/LoadingButton';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DateTimePicker from '@mui/lab/DateTimePicker';

import { apiGet, apiPost } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import { Utils } from 'common/Utils';
import { PrivateRoutesContext } from "app/PrivateRoutesContext";
import EmailAttachments from "../Table/EmailAttachments";
import AximTooltip from "components/AximTooltip";
import ConfirmDialog from "components/Content/Modal/ConfirmDialog"

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SendIcon from '@mui/icons-material/Send';

import moment from "moment";

export default function SendEmailDialog({isOpen, handleClose, names}) {
    const { companyId, error } = useContext(PrivateRoutesContext);

    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedBody, setSelectedBody] = useState('');
    const [attachmentCount, setAttachmentCount] = useState(0);
    const [attachmentRefresh, setAttachmentRefresh] = useState(0);

    const [recipients, setRecipients] = useState([]);
    const [emailAddress, setEmailAddress] = useState('');
    const [doOnlySendNew, setDoOnlySendNew] = useState(true);
    const [delayDateTime, setDelayDateTime] = useState(null);

    const [doShowConfirm, setDoShowConfirm] = useState(false);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);

    const [isSendEmailDisabled, setIsSendEmailDisabled] = useState(true);
    const [isCloseDisabled, setIsCloseDisabled] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    useEffect(() => {
        apiGet(apiUrls.getEmailTemplates(companyId)).then(
            (response) => {
                setTemplates(response.data);
            }
        ).catch(
          (response) => {
            error.setTitle("An Error Occurred");
            error.setText("An Error occurred loading templates.");
            error.setDoShowError(true);
          }
        );
    },[]);

    useEffect(() => {
        //console.log(names, "names");
        setRecipients([...names]);
    },[names])

    useEffect(() => {
        if (templates.length > 0) {
            setSelectedTemplateId(templates[0].emailTemplateId);
        }
        setDelayDateTime(null);
    },[templates,isOpen])

    useEffect(() => {
        //console.log(selectedTemplate, "selectedTemplate");

        if (selectedTemplate) {
            setSelectedSubject(selectedTemplate.emailTemplateSubject);
            setSelectedBody(selectedTemplate.emailTemplateBody);
        }
        else {
            setSelectedSubject("");
            setSelectedBody("");
        }

        setAttachmentRefresh(attachmentRefresh + 1);
        setIsSendEmailDisabled(!isTestEmailSent);
        setDoOnlySendNew(true);
    },[selectedTemplateId]);

    const selectedTemplate = useMemo(() => 
        templates.find(c => c.emailTemplateId === selectedTemplateId)
    ,[selectedTemplateId]);

    const isValidEmailAddress = useMemo(() => 
        Utils.isValidEmailAddress(emailAddress) &&
            recipients.findIndex(r => r.emailAddress === emailAddress) < 0
    ,[emailAddress]);

    const isTestEmailSent = useMemo(() => {
        return selectedTemplate && moment(selectedTemplate.testEmailSendTime) > moment(selectedTemplate.updateDate);
    },[selectedTemplate]);

    const handleAddRecipient = () => {
        setRecipients([...recipients, { "emailAddress": emailAddress, "isManuallyAdded": true }]);
        setEmailAddress('');
    }

    const removeRecipient = (emailAddress) => {
        let newRecipients = recipients.filter(r => r.emailAddress !== emailAddress);
        setRecipients(newRecipients);
    }

    const handleUpdateAttachmentCount = (attachmentCount) => {
        setAttachmentCount(attachmentCount);
    }

    const promptSendEmail = () => {
        setIsSendEmailDisabled(true);
        setIsCloseDisabled(true);

        setConfirmTitle("Send Email");
        setConfirmText(`Are you sure you would like to send email?`);
        setDoShowConfirm(true);
        setConfirmAction(() => (result) => handleSendEmail(result));
    }

    const handleSendEmail = (result) => {
        setDoShowConfirm(false);

        if (result) {
            setIsSendingEmail(true);
            // console.log(recipients);
            apiPost(apiUrls.sendEmail(companyId, selectedTemplateId), { 
                    emailAddresses: recipients.map(r => r.insuredID ?? r.emailAddress),
                    delayDateTime,
                    overrideSentEmail : !doOnlySendNew
                })
                .then((response) => {

                })
                .catch((response) => {

                })
                .finally(() => {
                    setIsSendEmailDisabled(false);
                    setIsCloseDisabled(false);
                    setIsSendingEmail(false);
                })
        }
        else {
            setIsSendEmailDisabled(false);
            setIsCloseDisabled(false);
        }
    }

    const handleConfirmClose = (result) => {
        confirmAction(result);
    }

    return (
        <>
            <Dialog
                open={isOpen}
                maxWidth="md"
                fullWidth="true"
            >
                <DialogTitle>Send Email</DialogTitle>
                <DialogContent>
                    <Box className="container">
                        <Box className="row">
                            <Box className="col">
                                <Accordion>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                    >
                                        <strong>Recipients</strong>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box className="row">
                                            <Box className="col-4">
                                                <TextField 
                                                    variant="standard"
                                                    value={emailAddress}
                                                    onChange={e => setEmailAddress(e.target.value)}
                                                    sx={{width: "100%"}}/>
                                            </Box>
                                            <Box className="col-8">
                                                <Button
                                                    onClick={handleAddRecipient}
                                                    variant="outlined"
                                                    disabled={!isValidEmailAddress}>
                                                    Add Recipient
                                                </Button>
                                            </Box>
                                        </Box>
                                        <Box className="row">
                                            <Box className="col-1"></Box>
                                            <Box className="col-3"><strong>First Name</strong></Box>
                                            <Box className="col-3"><strong>Last Name</strong></Box>
                                            <Box className="col-5"><strong>Email</strong></Box>
                                        </Box>
                                        {recipients.map(r => {
                                            return (
                                                <Box className="row">
                                                    <Box className="col-1">
                                                        {r.isManuallyAdded ?
                                                            <IconButton
                                                                onClick={() => removeRecipient(r.emailAddress)}
                                                            >
                                                                <DeleteOutlinedIcon />
                                                            </IconButton> : null}
                                                    </Box>
                                                    <Box className="col-3">{r.firstName}</Box>
                                                    <Box className="col-3">{r.lastName}</Box>
                                                    <Box className="col-5">{r.emailAddress}</Box>
                                                </Box>
                                            );
                                        })}
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        </Box>

                        <Box className="row mt-5">
                            <Box className="col-4 text-end">
                                <strong>Template</strong>
                            </Box>
                            <Box className="col-8">
                                <Select 
                                    variant="standard"
                                    value={selectedTemplateId}
                                    onChange={e => setSelectedTemplateId(e.target.value)}
                                    sx={{width: "100%"}}>
                                    {templates.map(t => <MenuItem key={t.emailTemplateId} value={t.emailTemplateId}>{t.emailTemplateDescription}</MenuItem>)}
                                </Select>
                            </Box>
                        </Box>
                        <Box className="row">
                            <Box className="col-4 text-end">
                                <strong>Email Subject</strong>
                            </Box>
                            <Box className="col-8">
                                <TextField
                                    disabled
                                    variant="standard"
                                    value={selectedSubject}
                                    sx={{width: "100%"}}/>
                            </Box>
                        </Box>
                        <Box className="row">
                            <Box className="col-4 text-end">
                                <strong>Email Body</strong>
                            </Box>
                            <Box className="col-8">
                                <ReactQuill value={selectedBody}
                                    modules={{toolbar: false}}
                                    readOnly />
                            </Box>
                        </Box>
                        <Box className={`row ${attachmentCount === 0 ? "d-none" : ""}`}>
                            <Box className="col">
                            <Accordion>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                    >
                                        <strong>Attachments</strong>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box className="row">
                                            <EmailAttachments
                                                isEditable={false}
                                                templateId={selectedTemplateId}
                                                refresh={attachmentRefresh}
                                                updateAttachmentCount={handleUpdateAttachmentCount} />
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        </Box>
                        {selectedTemplate && selectedTemplate.isWelcomeEmail ?
                        <Box className="row">
                            <Box className="col-4 text-end">
                            </Box>
                            <Box className="col-8">
                                <FormControlLabel control={
                                    <Checkbox checked={doOnlySendNew} onChange={e => setDoOnlySendNew(e.target.checked)} />
                                } label="Only send welcome email to new employees" />
                            </Box>
                        </Box> : null}
                        <Box className="row">
                            <Box className="col mt-4 d-flex align-items-center">
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DateTimePicker
                                        label="Delay Date and Time"
                                        value={delayDateTime}
                                        onChange={(value) => setDelayDateTime(value)}
                                        renderInput={(params) => <TextField variant="standard" {...params} />}
                                    />
                                </LocalizationProvider>
                                <Button className="ms-2" onClick={() => setDelayDateTime(null)} variant="outlined">
                                    Clear
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <AximTooltip arrow title={isTestEmailSent ? "" : "Send test email to enable Sending Emails"}>
                        <span className="me-2">
                            <LoadingButton 
                                onClick={promptSendEmail} 
                                color="primary"
                                variant="outlined"
                                disabled={isSendEmailDisabled}
                                startIcon={<SendIcon />}
                                loading={isSendingEmail}
                                loadingPosition="start"
                            >
                                Send Email{recipients.length === 1 ? "" : "s"} ({recipients.length})
                            </LoadingButton>
                        </span>
                    </AximTooltip>
                    <Button onClick={handleClose} color="primary" variant="outlined" disabled={isCloseDisabled}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                    doShowConfirm={doShowConfirm}
                    handleClose={handleConfirmClose}
                    confirmTitle={confirmTitle}
                    confirmText={confirmText} />
        </>
    );
}